import { useState } from "react";
import { Sparkles, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { protectedPost } from "@/lib/api";
import { FileCard } from "@/components/files/FileCard";
import { cn } from "@/lib/utils";

const QUALITIES = ["low", "medium", "high"];

export function GeneratedGallery({ files, workspace, enabled, onGenerated, onDelete, onOpen }) {
  const [prompt, setPrompt] = useState("");
  const [quality, setQuality] = useState("medium");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    const p = prompt.trim();
    if (!p || busy) return;
    setBusy(true);
    try {
      const rec = await protectedPost("/images/generate", { prompt: p, workspace, quality });
      onGenerated?.(rec);
      setPrompt("");
      toast.success("Rendered and saved to your gallery, Sir.");
    } catch (e) {
      toast.error(e.message || "Image generation failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="generated-gallery">
      <div className="jv-panel hud-chamfer hud-bracket p-3.5" data-testid="image-generator">
        <div className="flex items-center gap-2 mb-2 font-brand text-[11px] font-semibold uppercase tracking-[0.14em] text-jarvis-violet">
          <Wand2 className="w-4 h-4" /> Render an image
          <span className="ml-auto font-mono text-[9px] tracking-wider text-slate-500 normal-case">gpt-image-1 · Universal Key</span>
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <input value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && generate()}
            disabled={!enabled || busy} data-testid="image-prompt-input"
            placeholder={enabled ? "Describe what Jarvis should render…" : "Image generation is not configured"}
            className="flex-1 bg-black/40 border border-jarvis-purple/30 px-3.5 py-2.5 text-sm font-ui text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-jarvis-purple/70 focus:shadow-[0_0_16px_rgba(118,88,255,0.25)] disabled:opacity-50" />
          <div className="flex gap-2">
            <div className="flex border border-jarvis-purple/25" role="radiogroup" aria-label="Quality">
              {QUALITIES.map((q) => (
                <button key={q} onClick={() => setQuality(q)} role="radio" aria-checked={quality === q} data-testid={`image-quality-${q}`}
                  className={cn("px-2.5 font-mono text-[9px] uppercase tracking-wider transition-colors",
                    quality === q ? "bg-jarvis-purple/25 text-jarvis-violet" : "text-slate-500 hover:text-slate-300")}>
                  {q}
                </button>
              ))}
            </div>
            <button onClick={generate} disabled={!enabled || busy || !prompt.trim()} data-testid="image-generate-btn"
              className="flex items-center gap-2 font-brand text-[11px] font-semibold uppercase tracking-wider px-4 py-2.5 border border-jarvis-purple/50 text-jarvis-violet bg-jarvis-purple/15 hover:bg-jarvis-purple/25 disabled:opacity-40 disabled:cursor-not-allowed hud-chamfer">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busy ? "Rendering…" : "Generate"}
            </button>
          </div>
        </div>
        {busy && <p className="mt-2 font-num text-[11px] text-jarvis-violet/80 jv-pulse" data-testid="image-generating-note">Rendering may take up to a minute, Sir…</p>}
      </div>

      {files.length === 0 ? (
        <div className="jv-panel hud-chamfer p-10 text-center" data-testid="generated-empty">
          <Sparkles className="w-8 h-8 text-slate-600 mx-auto mb-3" />
          <p className="font-ui text-sm text-slate-400">No renders yet, Sir.</p>
          <p className="font-ui text-[13px] text-slate-600 mt-1">Type a prompt above, or say “generate image of …” in the chat dock.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3" data-testid="generated-grid">
          {files.map((f) => <FileCard key={f.id} file={f} onDelete={onDelete} onOpen={onOpen} />)}
        </div>
      )}
    </div>
  );
}
